// The English dictionary defines the shape; pl.ts must satisfy it, so a missing
// translation is a compile error, not a blank label in production.

import { pushEn } from './push-strings';

export const en = {
  // The strings a push notification can contain live in their own module so the
  // service worker can have them without the other ~250.
  ...pushEn,

  'app.name': 'Dialano',

  'threads.title': 'Conversations',
  'threads.empty.title': 'No companies yet',
  'threads.empty.body': 'Add a company by its phone number and start talking.',
  'threads.add': 'Add a company',
  'threads.you': 'You: ',
  'threads.pending': 'sending…',
  'threads.readOnly': 'read-only',
  'threads.paused': 'paused',

  'add.title': 'Add a company',
  'add.numberLabel': 'Company phone number',
  'add.numberHint': 'International format, e.g. +48 600 100 200',
  'add.error.own_as_company': "That's your own number — enter the company's number here.",

  'onboarding.title': 'Your phone number',
  'onboarding.body':
    'Companies verify you by texting a code to this number. You give it once; it can be changed later in Settings.',
  'onboarding.continue': 'Continue',
  'settings.ownNumber': 'Your phone number',
  'settings.ownNumber.saved': 'Saved',
  'add.continue': 'Continue',
  'add.progress.looking_up': 'Looking up the number…',
  'add.progress.registering_device': 'Preparing this device…',
  'add.progress.verifying_code': 'Checking the code…',
  'add.codeTitle': 'Enter the code sent to {phone}',
  'add.codeHint': 'The company sent you a text message with a 6-digit code.',
  'add.verify': 'Verify',
  'add.done': 'Company added',
  'add.reducedTrust':
    'The company directory could not be refreshed, so adding new companies is paused. Existing conversations keep working.',
  'add.error.bad_request': 'This does not look like a valid international number.',
  'add.error.not_found': 'No company is registered at this number.',
  'add.error.entry_withdrawn': 'This company is no longer accepting new conversations.',
  'add.error.number_not_allowed': 'This number cannot be used here.',
  'add.error.rate_limited': 'Too many attempts. Try again in a moment.',
  'add.error.invalid_code': 'That code is not right. Check it and try again.',
  'add.error.code_expired': 'That code has expired. Start again to get a new one.',
  'add.error.no_network': 'No connection. Check your internet and try again.',
  'add.error.clock_unreliable':
    'Your device clock looks wrong, so directory data cannot be trusted. Fix the clock and try again.',
  'add.error.generic': 'Something went wrong. Try again.',

  'add.contacts.title': 'Companies among your contacts',
  'add.contacts.body':
    'Pick contacts and I will check which of their numbers belong to companies available here. The check happens on this device — the numbers are not sent anywhere.',
  'add.contacts.pick': 'Pick contacts to check',
  'add.contacts.checking': 'Checking…',
  'add.contacts.none': 'None of the picked numbers belongs to a company available here.',
  'add.contacts.error': 'The contact picker could not be opened. You can always type the number by hand.',

  'conv.composerHint': 'Message',
  'conv.send': 'Send',
  'conv.linkBlocked': 'Links cannot be sent here.',
  'conv.pending': 'Waiting to send',
  'conv.failed': 'Not sent',
  'conv.retry': 'Retry',
  'conv.discard': 'Discard',
  'conv.personAnswering': 'A person is answering',
  'conv.readOnlyBanner': 'This company has closed its channel. The conversation is read-only.',
  'conv.pausedBanner':
    'The company server key changed. Sending is paused until you review it in company settings.',
  'conv.quarantineBanner': 'New device waiting period — older history may arrive later.',
  'conv.reverifyBanner': 'This number needs to be verified again with this company.',
  'conv.reverify': 'Verify again',
  'conv.attachPhoto': 'Attach a photo',
  'conv.recordVoice': 'Record a voice message',
  'conv.recording': 'Recording… {s} s',
  'conv.recordingStop': 'Stop and send',
  'conv.recordingCancel': 'Cancel',
  'conv.micDenied': 'Microphone access was refused, so voice messages cannot be recorded.',

  'el.unknown': 'A newer version of the app is needed to display this content.',
  'el.voice.unplayable': 'This recording cannot be played on this device.',
  'el.voice.download': 'Download recording',
  'el.image.open': 'Open image',
  'el.question.answerHint': 'Your answer',
  'el.question.required': 'This question needs an answer.',
  'el.cart.total': 'Total',
  'el.cart.accept': 'Accept',
  'el.cart.adjust': 'Send changes',
  'el.cart.decline': 'Decline',
  'el.cart.note': 'Note (optional)',
  'el.cart.state.proposed': 'Awaiting your decision',
  'el.cart.state.accepted': 'Accepted',
  'el.cart.state.declined': 'Declined',
  'el.cart.state.amended': 'Changes sent',
  'el.cart.state.superseded': 'Replaced by a newer cart',
  'el.cart.conflict': 'This cart has changed on the company side. Review the newest version.',
  'el.order.state.received': 'Received',
  'el.order.state.processing': 'Being prepared',
  'el.order.state.shipped': 'Shipped',
  'el.order.state.delivered': 'Delivered',
  'el.order.state.cancelled': 'Cancelled',
  'el.order.expected': 'Expected delivery: {date}',
  'el.order.reference': 'Reference: {ref}',
  'el.payment.info': 'Payments are not handled in this app.',
  'el.location.copy': 'Copy coordinates',
  'el.location.copied': 'Copied',
  'el.calendar.title': 'Appointment',
  'el.calendar.cancelled': 'Cancelled',
  'el.calendar.download': 'Add to calendar (.ics)',
  'el.video.download': 'Download video',

  'contact.title': 'Company',
  'contact.number': 'Phone number',
  'contact.server': 'Server address',
  'contact.notifications': 'Notification categories',
  'contact.muted': 'Muted',
  'contact.orders': 'Orders',
  'contact.export': 'Download history',
  'contact.delete': 'Delete company',
  'contact.delete.confirm':
    'Delete this company? The conversation and its history disappear from this device.',
  'contact.keyChange.title': 'Server key changed',
  'contact.keyChange.body':
    'The company server identifies itself with a different key than before. This can be a legitimate server move — or someone impersonating it. Sending stays paused until you accept the new key.',
  'contact.keyChange.trust': 'Trust the new key',
  'contact.moved.signed': 'This company moved. The conversation follows automatically.',
  'contact.moved.waiting': 'This company reports moving. Sending is paused during the waiting period.',
  'contact.withdrawn': 'This company has closed its channel. History stays on this device.',

  'orders.title': 'Orders',
  'el.request.title': 'Asked about:',
  // ——— the staff surface (optional; not part of the buyer's app) ———
  'staff.title': 'Work account',
  'staff.server': 'Company server address',
  'staff.login': 'Login',
  'staff.password': 'Password',
  'staff.signIn': 'Sign in',
  'staff.signOut': 'Sign out',
  'staff.buyers': 'Buyers',
  'staff.noBuyers': 'No conversations on this server yet.',
  'staff.pages': 'Pages',
  'staff.published': 'Published: {n} of 6',
  'staff.live': 'live',
  'staff.draft': 'draft',
  'staff.edit': 'Edit',
  'staff.publish': 'Publish',
  'staff.remove': 'Remove',
  'staff.newPage': 'New page',
  'staff.pageTitle': 'Menu label',
  'staff.elements': 'Elements',
  'staff.save': 'Save draft',
  'staff.preview': 'What the buyer sees',
  'staff.jsonArray': 'Elements must be a JSON array.',
  'staff.slotHint':
    'A card places a control with <ctl i="0">, filled by entry 0 of its controls list. The wording on the button is the app\'s, not yours.',
  // ——— company pages ———
  'views.fromSupplier': 'From this supplier',
  'views.loading': 'Opening…',
  // Not a warning to act on: a statement of what the buttons are doing. The page is
  // readable; ordering against a price we could not re-check is what is switched off.
  'views.offline': 'Shown from this device. No connection, so nothing can be ordered here right now.',
  'views.goneTitle': 'Page unavailable',
  'views.goneBody': 'This company is no longer publishing this page.',
  // Control captions. These are OURS, never the seller's: a seller places a button, we
  // write what is on it, so a harmless action can never carry a lying label.
  'views.control.quantity': 'Quantity',
  'views.control.requestItems': 'Ask for these items',
  'views.control.cartAccept': 'Order',
  'views.control.cartAmend': 'Change quantities',
  'views.control.cartDecline': 'Decline',
  'views.control.answer': 'Answer',
  'views.control.calendarAdd': 'Add to calendar',
  'views.control.itemQuantity': 'Quantity of {name}',
  'views.requestSent': 'Asked about {count} item(s).',

  'orders.empty': 'No orders in this conversation yet.',

  'settings.title': 'Settings',
  'settings.language': 'Language',
  'settings.language.system': 'System',
  'settings.language.en': 'English',
  'settings.language.pl': 'Polski',
  'settings.diagnostics': 'Diagnostics',
  'settings.about': 'Dialano — talking with companies: ask, order, book.',
  'settings.push.title': 'Notifications',
  'settings.push.body':
    'Companies can notify you about new messages. A notification may include the message content, encrypted so that only this device can read it — the wake service passes it on without being able to look inside.',
  'settings.push.enable': 'Turn on notifications',
  'settings.push.on': 'Notifications are on',
  'settings.push.off': 'Turn off',
  'settings.push.denied': 'Notifications are blocked for this app in the browser settings.',
  'settings.push.unsupported': 'This browser does not support notifications for installed apps.',
  'push.nudge.body': 'Turn on notifications — replies will reach you even with the app closed.',
  'push.nudge.enable': 'Turn on',
  'push.nudge.retry': 'Try again',
  'push.fail.dismissed': 'The permission prompt was dismissed — tap again and allow notifications.',
  'push.fail.noGatewayKey':
    'The directory has no wake-gateway key yet. Check your connection and try again in a moment.',
  'push.fail.subscribe': 'The browser refused the subscription. Try again in a moment.',
  'push.fail.notReady': 'The app is still installing in the background — wait a moment and try again.',

  'diag.title': 'Diagnostics',
  'diag.directory': 'Directory',
  'diag.manifestVersion': 'Manifest version',
  'diag.fetchedAt': 'Fetched',
  'diag.fromDomain': 'From domain',
  'diag.validUntil': 'Valid until',
  'diag.entries': 'Cached entries',
  'diag.lastError': 'Last error',
  'diag.push': 'Notifications',
  'diag.push.supported': 'Supported here',
  'diag.push.permission': 'System permission',
  'diag.push.subscribed': 'Subscription exists',
  'diag.push.vapid': 'Gateway key in directory',
  'diag.yes': 'yes',
  'diag.no': 'no',
  'diag.offline': 'Offline copy',
  'diag.offline.registered': 'Saved on this device',
  'diag.offline.controlling': 'In charge of this page',
  'diag.offline.state': 'State',
  'diag.offline.files': 'Files saved',
  'diag.offline.startPage': 'Start page saved',
  'diag.offline.servedLaunch': 'Served this launch',
  'diag.offline.note':
    'Opening without a connection needs "In charge of this page" to say yes. "Served this launch" is normally no on the launch that first saved the copy. Repairing downloads a fresh copy; it never touches your messages.',
  'diag.offline.repair': 'Repair offline copy',
  'diag.storage': 'Storage',
  'diag.persisted': 'Protected from eviction',
  'diag.persisted.yes': 'yes',
  'diag.persisted.no': 'no — the browser may clear data under pressure',
  'diag.estimate': 'Used {used} of {quota}',
  'diag.recovery':
    'If this device loses its data, verify your number again with each company — history returns from their servers.',
  'diag.boot': 'This launch',
  'diag.boot.sw': 'Service worker start',
  'diag.boot.ttfb': 'First byte',
  'diag.boot.entry': 'Entry script running',
  'diag.boot.settings': 'Settings read',
  'diag.boot.mounted': 'First screen drawn',
  'diag.boot.note':
    'Times from the moment the launch began. Force-quit the app and reopen it to measure a cold start — a warm one says little.',

  'diag.contacts': 'Companies',
  'diag.cursor': 'Cursor',
  'diag.lastSequence': 'Last sequence',
  'diag.gap': 'Gap detected in the event stream',
  'diag.resync': 'Resync from start',
  'diag.syncError': 'Last sync error',

  'author.agent': 'assistant',
  'author.human': 'person',
  'author.system': 'system',

  'common.menu': 'Menu',
  'common.back': 'Back',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.delete': 'Delete',
  'common.retry': 'Retry',
  'common.updated': 'Dialano updated.',
  'common.reload': 'Reload',
} as const;

export type Dict = Record<keyof typeof en, string>;
